import { useUpdateOrderQuoteMutation } from '@codezo/graphql';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { EMPTY_ADDRESS, type AddressData } from '../../../../components/ui/address-form';
import { AddressPicker } from '../../../../components/ui/address-picker';
import { Button } from '../../../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../../../components/ui/dialog';

interface EditOrderQuoteAddressValue {
	addressLine1?: string | null;
	addressLine2?: string | null;
	city?: string | null;
	stateCode?: string | null;
	postalCode?: string | null;
	countryCode?: string | null;
	googlePlaceId?: string | null;
	latitude?: number | null;
	longitude?: number | null;
}

interface EditOrderQuoteAddressModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	quoteId: string;
	/** Whose saved addresses are offered beside the custom form. */
	organizationId?: string | null;
	initialValue: EditOrderQuoteAddressValue | null;
	onSuccess: () => void;
}

export function EditOrderQuoteAddressModal({
	open,
	onOpenChange,
	quoteId,
	organizationId,
	initialValue,
	onSuccess,
}: EditOrderQuoteAddressModalProps) {
	const [address, setAddress] = useState<AddressData>(EMPTY_ADDRESS);
	const [updateQuote, { loading }] = useUpdateOrderQuoteMutation();

	// Read through a ref so an unrelated parent re-render can't reset the form
	// mid-edit — see the matching note in EditOrderQuoteDetailsModal.
	const initialRef = useRef(initialValue);
	initialRef.current = initialValue;

	useEffect(() => {
		if (!open) return;
		const v = initialRef.current;
		setAddress({
			addressLine1: v?.addressLine1 || '',
			addressLine2: v?.addressLine2 || '',
			city: v?.city || '',
			stateCode: v?.stateCode || '',
			postalCode: v?.postalCode || '',
			countryCode: v?.countryCode || '',
			// Carried through the edit: updateOrderQuote replaces the whole address,
			// so dropping these would discard the geocoding on any unrelated tweak.
			// Picking a new autocomplete suggestion overwrites all three together.
			googlePlaceId: v?.googlePlaceId || '',
			latitude: v?.latitude ?? null,
			longitude: v?.longitude ?? null,
		});
	}, [open]);

	const handleSubmit = async () => {
		if (!address.addressLine1.trim()) return;
		try {
			const { data } = await updateQuote({
				variables: {
					id: quoteId,
					input: {
						deliveryAddress: {
							addressLine1: address.addressLine1.trim(),
							addressLine2: address.addressLine2 || undefined,
							city: address.city || undefined,
							stateCode: address.stateCode || undefined,
							postalCode: address.postalCode || undefined,
							countryCode: address.countryCode || undefined,
							googlePlaceId: address.googlePlaceId || undefined,
							latitude: address.latitude,
							longitude: address.longitude,
						},
					},
				},
			});

			// The API reports permission/validation failures as success: false
			// rather than throwing, so a thrown error isn't the only failure mode.
			if (data?.updateOrderQuote?.success) {
				toast.success('Delivery address updated');
				onSuccess();
				onOpenChange(false);
			} else {
				toast.error(data?.updateOrderQuote?.message || 'Failed to update address');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update address');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{initialValue?.addressLine1
							? 'Edit delivery address'
							: 'Add delivery address'}
					</DialogTitle>
				</DialogHeader>
				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						{/* Editing keeps whatever the quote already holds — the picker
						    only offers your saved addresses beside it. */}
						<AddressPicker
							organizationId={organizationId}
							value={address}
							onChange={setAddress}
							skip={!open}
							autoSelectPrimary={false}
						/>
					</DialogBody>
					<DialogFooter>
						<Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button type="submit" disabled={!address.addressLine1.trim() || loading}>
							{loading ? 'Saving…' : 'Save'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
